import { useRef, useState, useEffect, useCallback } from 'react'
import { Eraser, Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils/cn'

interface SignaturePadProps {
  onSignatureCapture: (dataUrl: string) => void
  onClear?: () => void
  height?: number
  className?: string
}

export function SignaturePad({
  onSignatureCapture,
  onClear,
  height = 150,
  className,
}: SignaturePadProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null)
  const [hasSignature, setHasSignature] = useState(false)
  // Locks the Confirm button after a successful capture. Cleared by Clear or
  // by any new stroke — if the signer wants to change their mark, drawing
  // again re-enables Confirm so the latest version gets sent.
  const [hasConfirmed, setHasConfirmed] = useState(false)
  const isDrawingRef = useRef(false)
  const [canvasSize, setCanvasSize] = useState({ width: 300, height })

  // Measure container and set canvas size
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth - 16 // Account for padding
        setCanvasSize({ width: containerWidth, height })
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [height])

  // Initialize canvas context and styles
  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return null

    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    // Set up canvas for high DPI displays
    const dpr = window.devicePixelRatio || 1
    canvas.width = canvasSize.width * dpr
    canvas.height = canvasSize.height * dpr
    ctx.scale(dpr, dpr)

    // Set drawing styles
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // Fill with white background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height)

    ctxRef.current = ctx
    return ctx
  }, [canvasSize])

  // Initialize on mount and when size changes
  useEffect(() => {
    initializeCanvas()
  }, [initializeCanvas])

  // Get coordinates from touch event
  const getCoordinates = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()

    return {
      x: (clientX - rect.left) * (canvasSize.width / rect.width),
      y: (clientY - rect.top) * (canvasSize.height / rect.height),
    }
  }, [canvasSize])

  // Set up touch event listeners
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault()
      const ctx = ctxRef.current
      if (!ctx) return

      const touch = e.touches[0]
      const { x, y } = getCoordinates(touch.clientX, touch.clientY)

      ctx.beginPath()
      ctx.moveTo(x, y)
      isDrawingRef.current = true
      setHasSignature(true)
      setHasConfirmed(false)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDrawingRef.current) return
      e.preventDefault()

      const ctx = ctxRef.current
      if (!ctx) return

      const touch = e.touches[0]
      const { x, y } = getCoordinates(touch.clientX, touch.clientY)

      ctx.lineTo(x, y)
      ctx.stroke()
    }

    const handleTouchEnd = () => {
      isDrawingRef.current = false
    }

    // Add touch event listeners with passive: false
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd)
    canvas.addEventListener('touchcancel', handleTouchEnd)

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
      canvas.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [getCoordinates])

  // Mouse event handlers
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    const ctx = ctxRef.current
    if (!ctx) return

    const { x, y } = getCoordinates(event.clientX, event.clientY)
    ctx.beginPath()
    ctx.moveTo(x, y)
    isDrawingRef.current = true
    setHasSignature(true)
    setHasConfirmed(false)
  }, [getCoordinates])

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!isDrawingRef.current) return

    const ctx = ctxRef.current
    if (!ctx) return

    const { x, y } = getCoordinates(event.clientX, event.clientY)
    ctx.lineTo(x, y)
    ctx.stroke()
  }, [getCoordinates])

  const handleMouseUp = useCallback(() => {
    isDrawingRef.current = false
  }, [])

  const clearSignature = useCallback(() => {
    const ctx = ctxRef.current
    if (!ctx) return

    // Re-initialize to clear and reset styles
    initializeCanvas()
    setHasSignature(false)
    setHasConfirmed(false)
    onClear?.()
  }, [initializeCanvas, onClear])

  const saveSignature = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const dataUrl = canvas.toDataURL('image/png')
    onSignatureCapture(dataUrl)
    setHasConfirmed(true)
  }, [onSignatureCapture])

  return (
    <div ref={containerRef} className={cn('space-y-2', className)}>
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-gray-900">
        <canvas
          ref={canvasRef}
          className="touch-none cursor-crosshair"
          style={{ width: canvasSize.width, height: canvasSize.height }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
        <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-1">
          Sign above
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearSignature}
          disabled={!hasSignature}
        >
          <Eraser className="h-4 w-4 mr-1" />
          Clear
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={saveSignature}
          disabled={!hasSignature || hasConfirmed}
          className="flex-1"
        >
          <Check className="h-4 w-4 mr-1" />
          {hasConfirmed ? 'Signature Confirmed' : 'Confirm Signature'}
        </Button>
      </div>
    </div>
  )
}
